import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, AlertOctagon, Eye, XCircle, CheckCircle2, Zap, TrendingUp, FileText, ShieldCheck, ArrowRight, ChevronDown, ChevronRight, Info, Database, Shield, Activity } from 'lucide-react';
import { StatCard } from '../../../../shared/StatCard';
import { Badge } from '../../../../../components/ui/Badge';
import { Card } from '../../../../../components/ui/Card';
import { MODULE_CONFIG, PIPELINE_STAGES } from '../shared';
import type { GovernanceStats, ModuleHealth, FamilyHealth, FamilyTriageItem, UnifiedQuestionRow, SystemStats } from '../hooks/useGovernanceData';
import type { IssueDomain, ShadowImpactReport } from '../../../../../governance';
import { getGuardGovernanceIssues } from '../../../../../practice/logic/guardGovernanceBridge';
interface OverviewDashboardProps {
  rows: UnifiedQuestionRow[];
  stats: GovernanceStats;
  systemStats: SystemStats;
  moduleHealth: ModuleHealth[];
  familyHealthByModule: Record<string, FamilyHealth[]>;
  questionTypeDistribution: Map<string, number>;
  visualTypeDistribution: Map<string, number>;
  topBlockers: UnifiedQuestionRow[];
  familyTriage: FamilyTriageItem[];
  pipelineIssueCounts: Record<IssueDomain, number> | null;
  shadowImpactReport?: ShadowImpactReport | null;
  isRunning: boolean;
  onNavigateToQuestion: (filter?: string) => void;
}
export function OverviewDashboard({
  rows,
  stats,
  systemStats,
  moduleHealth,
  familyHealthByModule,
  questionTypeDistribution,
  visualTypeDistribution,
  topBlockers,
  familyTriage,
  pipelineIssueCounts,
  shadowImpactReport,
  isRunning,
  onNavigateToQuestion
}: OverviewDashboardProps) {
  const isHealthy = stats.fail === 0 && stats.review === 0;
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [expandedSeeds, setExpandedSeeds] = useState<Set<string>>(new Set());
  const [showOnlyModulesWithIssues, setShowOnlyModulesWithIssues] = useState(true);
  const [sortUrgency, setSortUrgency] = useState(true);
  const [isDiagnoseOpen, setIsDiagnoseOpen] = useState(true);
  const [isSysteemOpen, setIsSysteemOpen] = useState(false);
  // P-9: Guard → Governance feedback data
  const guardSummary = useMemo(() => getGuardGovernanceIssues(), []);
  const toggleModule = (code: string) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);else next.add(code);
      return next;
    });
  };
  const toggleSeed = (familyId: string) => {
    setExpandedSeeds(prev => {
      const next = new Set(prev);
      if (next.has(familyId)) next.delete(familyId);else next.add(familyId);
      return next;
    });
  };
  const triageByModule = useMemo(() => {
    const map = new Map<string, FamilyTriageItem[]>();
    for (const f of familyTriage) {
      if (!map.has(f.moduleCode)) map.set(f.moduleCode, []);
      map.get(f.moduleCode)!.push(f);
    }
    return map;
  }, [familyTriage]);
  const moduleRows = useMemo(() => {
    let mods = moduleHealth.filter(m => {
      if (showOnlyModulesWithIssues) return triageByModule.has(m.code);
      return true;
    });
    if (sortUrgency) {
      mods.sort((a, b) => {
        const aUrgency = triageByModule.get(a.code)?.reduce((sum, f) => sum + f.urgencyScore, 0) || 0;
        const bUrgency = triageByModule.get(b.code)?.reduce((sum, f) => sum + f.urgencyScore, 0) || 0;
        return bUrgency - aUrgency;
      });
    } else {
      mods.sort((a, b) => a.code.localeCompare(b.code));
    }
    return mods;
  }, [moduleHealth, triageByModule, showOnlyModulesWithIssues, sortUrgency]);
  // Total findings across all pipeline stages
  const totalFindings = pipelineIssueCounts ? Object.values(pipelineIssueCounts).reduce((sum, c) => sum + c, 0) : 0;
  return <div className="relative max-w-7xl mx-auto pb-12" data-id="element-5226" data-mp="11tznawc94kxj">
      {/* Vertical Connector Line for Narrative Flow */}
      <div className="absolute left-4 top-8 bottom-8 w-px bg-gray-200 hidden sm:block" data-id="element-5227" data-mp="1lujb5c79kuw7" />

      <div className="space-y-12" data-id="element-5228" data-mp="1pvbk04c84xry">
        {/* ZONE A: Acties vandaag */}
        <section className="relative" data-id="element-5229" data-mp="1un31zqhbdv6h">
          <header className="flex items-start gap-4 mb-6" data-id="element-5230" data-mp="1uo4pwvjfic5m">
            <div className="relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-600 text-sm font-bold border-4 border-white shrink-0 hidden sm:flex shadow-sm" data-id="element-5231" data-mp="1zmpefxpbj7e8">
              1
            </div>
            <div className="pt-1" data-id="element-5232" data-mp="16yab48fjudiq">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-5233" data-mp="1bwv4bnwdvwxo">
                Acties Vandaag
              </h2>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-5234" data-mp="1gwhgrv4e195f">
                Wat vereist directe aandacht in de content pipeline?
              </p>
            </div>
          </header>

          <div className="sm:ml-12 space-y-6" data-id="element-5235" data-mp="2sb4soi0mlloi">
            {/* Health Banner — question-level summary */}
            <div className={`flex items-center gap-4 px-5 py-4 rounded-2xl border shadow-sm transition-colors ${isHealthy ? 'bg-emerald-50/50 border-emerald-200' : stats.fail > 0 ? 'bg-red-50/50 border-red-200' : 'bg-amber-50/50 border-amber-200'}`} data-id="element-5236" data-mp="gni3elnyroxz">
              <div className={`p-2 rounded-full flex-shrink-0 ${isHealthy ? 'bg-emerald-100 text-emerald-600' : stats.fail > 0 ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-5237" data-mp="6vls28wva2bt">
                {isHealthy ? <ShieldCheck className="w-6 h-6" data-id="element-5238" data-mp="3p2jq9v89dcre" /> : stats.fail > 0 ? <XCircle className="w-6 h-6" data-id="element-5239" data-mp="3k5jgb98afpv0" /> : <AlertTriangle className="w-6 h-6" data-id="element-5240" data-mp="3f5nar5jxzqdc" />}
              </div>
              <div className="flex-1 min-w-0" data-id="element-5241" data-mp="1id4zkdhhcrg6">
                <span className={`text-base font-semibold ${isHealthy ? 'text-emerald-900' : stats.fail > 0 ? 'text-red-900' : 'text-amber-900'}`} data-id="element-5242" data-mp="1ibtzoor5o3uc">
                  {isHealthy ? 'Alle vragen voldoen aan de kwaliteitseisen' : stats.fail > 0 ? `${stats.fail} ${stats.fail === 1 ? 'vraag' : 'vragen'} afgekeurd` : 'Aandachtspunten gevonden'}
                </span>
                {!isHealthy && <div className={`text-sm mt-0.5 ${stats.fail > 0 ? 'text-red-700/80' : 'text-amber-700/80'}`} data-id="element-5243" data-mp="32vfkobmjcrki">
                    {[stats.review > 0 && `${stats.review} vereisen review`, totalFindings > 0 && `${totalFindings} bevindingen totaal`].filter(Boolean).join(' · ')}
                  </div>}
              </div>
            </div>

            <Card className="p-0 shadow-sm border-gray-200/60 overflow-hidden" data-id="element-5244" data-mp="2a6iev7zign8n">
              <div className="p-5 border-b border-gray-100 bg-white flex flex-col sm:flex-row sm:items-start justify-between gap-4" data-id="element-5245" data-mp="2a4e363vdejxt">
                <div data-id="element-5246" data-mp="1xoeazfjodbjn">
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-5247" data-mp="1xmux1vlhrcl1">
                    Werklijst — Module → Seed → Vraag
                  </h3>
                  <p className="text-[11px] text-gray-500 mt-1 max-w-2xl" data-id="element-5248" data-mp="2cisxhq2g7332">
                    Open een module om de probleem-seeds te zien. Open een seed
                    om de afgekeurde vragen te zien. Klik op een vraag voor
                    details.
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0" data-id="element-5249" data-mp="1tmaxymhcxyw9">
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer hover:text-gray-900" data-id="element-5250" data-mp="1tnm2wpmjhcoy">
                    <input type="checkbox" checked={showOnlyModulesWithIssues} onChange={e => setShowOnlyModulesWithIssues(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-5251" data-mp="1otq5ppe3ntuu" />
                    Alleen issues
                  </label>
                  <select value={sortUrgency ? 'urgency' : 'alpha'} onChange={e => setSortUrgency(e.target.value === 'urgency')} className="text-xs border-gray-200 rounded-md text-gray-600 py-1 pl-2 pr-6 focus:ring-blue-500 focus:border-blue-500" data-id="element-5252" data-mp="kl8kh8fkkj1o">
                    <option value="urgency" data-id="element-5253" data-mp="13c9jfwgowel8">Sorteer: Urgentie</option>
                    <option value="alpha" data-id="element-5254" data-mp="13d1ft9i42gck">Sorteer: A-Z</option>
                  </select>
                </div>
              </div>

              <div className="divide-y divide-gray-100" data-id="element-5255" data-mp="1ccy0l01dqv2c">
                {moduleRows.length === 0 ? <div className="p-8 text-center text-gray-500 text-sm" data-id="element-5256" data-mp="1hbre2sgo2m2i">
                    Geen modules gevonden met de huidige filters.
                  </div> : moduleRows.map(mod => {
                const isExpanded = expandedModules.has(mod.code);
                const modTriage = triageByModule.get(mod.code) || [];
                const config = MODULE_CONFIG[mod.code];
                const bgClass = config?.bgClass ?? 'bg-gray-400';
                const criticalCount = modTriage.reduce((sum, f) => sum + f.criticalCount, 0);
                const blockerCount = modTriage.reduce((sum, f) => sum + f.blockerCount, 0);
                const reviewCount = modTriage.reduce((sum, f) => sum + f.reviewCount, 0);
                const worstPct = modTriage.length > 0 ? Math.min(...modTriage.map(f => f.avgScore)) : 100;
                return <div key={mod.code} className="bg-white" data-id="element-5257" data-mp="1mpq08dxqtcyr">
                        <div className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => toggleModule(mod.code)} data-id="element-5258" data-mp="1mraz2fr3bi2f">
                          <div className="flex items-center gap-3" data-id="element-5259" data-mp="2amvo1vfogjw3">
                            <div className={`w-2.5 h-2.5 rounded-full ${bgClass}`} data-id="element-5260" data-mp="2al2ikjgsw5po" />
                            <Badge variant="neutral" className="font-mono font-bold" data-id="element-5261" data-mp="2kc6wdfuqpkix">
                              {mod.code}
                            </Badge>
                            <span className="text-sm text-gray-600 hidden sm:inline" data-id="element-5262" data-mp="2tbk7ecyiih5r">
                              {mod.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-4" data-id="element-5263" data-mp="37yiuxwu90rf7">
                            <div className="hidden md:flex items-center gap-1.5" data-id="element-5264" data-mp="3cydqidoyy56l">
                              {criticalCount > 0 && <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded" data-id="element-5265" data-mp="2z29uqfyg5os0">
                                  {criticalCount} critical
                                </span>}
                              {blockerCount > 0 && <span className="text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-100 px-1.5 py-0.5 rounded" data-id="element-5266" data-mp="2fcg96jmbs5w8">
                                  {blockerCount} blocker
                                </span>}
                              {reviewCount > 0 && <span className="text-[10px] font-semibold text-yellow-700 bg-yellow-50 border border-yellow-100 px-1.5 py-0.5 rounded" data-id="element-5267" data-mp="2b9temay9s6vc">
                                  {reviewCount} review
                                </span>}
                            </div>
                            <div className="text-right text-xs text-gray-500 w-32" data-id="element-5268" data-mp="74kxphise68l">
                              {modTriage.length > 0 ? <>
                                  <span className="font-medium text-gray-900" data-id="element-5269" data-mp="zrp7s0wqlar8">
                                    {modTriage.length}
                                  </span>{' '}
                                  van{' '}
                                  {familyHealthByModule[mod.code]?.length || 0}{' '}
                                  seeds
                                  <div className="text-[10px] text-red-500" data-id="element-5270" data-mp="3bijciqr9z2bc">
                                    worst: {worstPct}%
                                  </div>
                                </> : <span className="text-emerald-600 font-medium" data-id="element-5271" data-mp="1m2grbbbf89rq">
                                  Alle{' '}
                                  {familyHealthByModule[mod.code]?.length || 0}{' '}
                                  seeds OK
                                </span>}
                            </div>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-5272" data-mp="24rvt57yxl2x6" />
                          </div>
                        </div>

                        <AnimatePresence data-id="element-5273" data-mp="2ere9lefmu6kh">
                          {isExpanded && modTriage.length > 0 && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden bg-gray-50/30 border-t border-gray-100" data-id="element-5274" data-mp="2ir4ytdpigemw">
                              <div className="divide-y divide-gray-100/50" data-id="element-5275" data-mp="31kbp0h5ro5u1">
                                {modTriage.map((fam, idx) => {
                          const isSeedExpanded = expandedSeeds.has(fam.familyId);
                          return <div key={fam.familyId} data-id="element-5276" data-mp="2npj3n2373vfj" data-mpi={idx}>
                                      <div className="flex items-center justify-between p-2.5 pl-4 sm:pl-12 hover:bg-orange-50/40 transition-colors group relative" data-id="element-5277" data-mp="2is9gxu76xxve">
                                        <div className="hidden sm:block absolute left-6 top-0 bottom-0 w-px bg-gray-200" data-id="element-5278" data-mp="13208hj37tqhm" />
                                        <div className="hidden sm:block absolute left-6 top-1/2 w-4 h-px bg-gray-200" data-id="element-5279" data-mp="pbd71j62lmhu" />

                                        <div className="flex items-center gap-3 min-w-0" data-id="element-5280" data-mp="tbx4yoygwu51">
                                          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white border border-gray-200 text-[9px] font-bold text-gray-500 flex items-center justify-center shadow-sm" data-id="element-5281" data-mp="1h1reawjkw0kj">
                                            {idx + 1}
                                          </div>
                                          <div className="min-w-0" data-id="element-5282" data-mp="1cxtgqq9wixtl">
                                            <div className="flex items-center gap-2" data-id="element-5283" data-mp="3spr244ubapfz">
                                              <span className="font-mono text-xs font-bold text-gray-900" data-id="element-5284" data-mp="1krbush5zwp9">
                                                {fam.familyId}
                                              </span>
                                              <div className="hidden lg:flex items-center gap-1" data-id="element-5285" data-mp="1ybvabv98t0vk">
                                                {fam.topIssueCodes.slice(0, 2).map(code => <span key={code} className="text-[9px] font-mono text-gray-500 bg-white border border-gray-200 px-1 rounded truncate max-w-[120px]" title={code} data-id="element-5286" data-mp="1kjymw4iirf5i">
                                                      {code}
                                                    </span>)}
                                              </div>
                                            </div>
                                            <div className="text-[10px] text-gray-500 mt-0.5" data-id="element-5287" data-mp="rnpfll5mckw3">
                                              {fam.total} vragen ·{' '}
                                              <span className="text-emerald-600" data-id="element-5288" data-mp="10h1bs1z2ix9u">
                                                {fam.pass} pass
                                              </span>
                                              {fam.fail > 0 && <span className="text-red-600" data-id="element-5289" data-mp="8q51980hd4pq">
                                                  {' '}
                                                  · {fam.fail} fail
                                                </span>}
                                              {fam.review > 0 && <span className="text-amber-600" data-id="element-5290" data-mp="1t4f39kjzz0mk">
                                                  {' '}
                                                  · {fam.review} review
                                                </span>}
                                            </div>
                                          </div>
                                        </div>

                                        <div className="flex items-center gap-4 flex-shrink-0" data-id="element-5291" data-mp="1njnryiavnwmu">
                                          <div className="hidden md:flex items-center gap-1" data-id="element-5292" data-mp="1ikjjxuzow4y3">
                                            {fam.criticalCount > 0 && <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded" title="Kritiek" data-id="element-5293" data-mp="1wjj8l2n7cju9">
                                                <AlertOctagon className="w-3 h-3 inline mr-0.5" data-id="element-5294" data-mp="3ucgjv4yl2l3x" />
                                                {fam.criticalCount}
                                              </span>}
                                            {fam.blockerCount > 0 && <span className="text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-100 px-1.5 py-0.5 rounded" title="Blocker" data-id="element-5295" data-mp="3feng63o9118f">
                                                <XCircle className="w-3 h-3 inline mr-0.5" data-id="element-5296" data-mp="2rit2r4u9m0b6" />
                                                {fam.blockerCount}
                                              </span>}
                                            {fam.reviewCount > 0 && <span className="text-[10px] font-semibold text-yellow-700 bg-yellow-50 border border-yellow-100 px-1.5 py-0.5 rounded" title="Review" data-id="element-5297" data-mp="un52gxj6bzia">
                                                <Eye className="w-3 h-3 inline mr-0.5" data-id="element-5298" data-mp="1ta1e3g76fzns" />
                                                {fam.reviewCount}
                                              </span>}
                                          </div>
                                          <div className="w-10 text-right hidden sm:block" data-id="element-5299" data-mp="27zh2ipj8jt0b">
                                            <span className={`text-xs font-bold ${fam.avgScore >= 80 ? 'text-emerald-600' : fam.avgScore >= 60 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-5300" data-mp="2c0ityk6wv0rq">
                                              {fam.avgScore}%
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-2" data-id="element-5301" data-mp="o1zzfrdt1yq7">
                                            <button onClick={() => onNavigateToQuestion(fam.familyId)} className="text-[10px] font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors hidden sm:block" data-id="element-5302" data-mp="130k8lote2as1">
                                              Open in Vragen →
                                            </button>
                                            <button onClick={() => toggleSeed(fam.familyId)} className="p-1 rounded hover:bg-gray-200 transition-colors" data-id="element-5303" data-mp="3iroi5nzzgmpf">
                                              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isSeedExpanded ? 'rotate-180' : ''}`} data-id="element-5304" data-mp="q1l1r0y4l6sh" />
                                            </button>
                                          </div>
                                        </div>
                                      </div>

                                      <AnimatePresence data-id="element-5305" data-mp="7c4nzbxfjhu4">
                                        {isSeedExpanded && <motion.div initial={{
                                height: 0,
                                opacity: 0
                              }} animate={{
                                height: 'auto',
                                opacity: 1
                              }} exit={{
                                height: 0,
                                opacity: 0
                              }} transition={{
                                duration: 0.2
                              }} className="overflow-hidden bg-white border-y border-gray-100/50" data-id="element-5306" data-mp="1dqngo11dklja">
                                            <div className="py-2 space-y-1 relative" data-id="element-5307" data-mp="8pwccfxvutl6">
                                              <div className="hidden sm:block absolute left-6 top-0 bottom-0 w-px bg-gray-200" data-id="element-5308" data-mp="3r2wigv4iqhu" />

                                              {(() => {
                                    const qs = rows.filter(r => r.familyId === fam.familyId && (r.verdict !== 'PASS' || r.blockerCount > 0 || r.warningCount > 0));
                                    const displayQs = qs.slice(0, 5);
                                    return <>
                                                    {displayQs.map(q => {
                                        const firstBlocker = q.pipelineIssues.find(i => i.kind === 'blocker' || i.kind === 'critical') || q.pipelineIssues.find(i => i.kind === 'coach');
                                        return <div key={q.questionId} onClick={() => onNavigateToQuestion(q.questionId)} className="ml-4 sm:ml-20 mr-4 p-2 rounded-lg border border-gray-100 hover:border-red-200 hover:bg-red-50/40 cursor-pointer transition-colors flex items-center gap-3 group" data-id="element-5309" data-mp="3odhdpka4gras">
                                                          <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" data-id="element-5310" data-mp="3dp4p489uadc9" />
                                                          <div className="min-w-0 flex-1" data-id="element-5311" data-mp="1jdg6nw4efsn4">
                                                            <div className="flex items-center gap-2 mb-0.5" data-id="element-5312" data-mp="1ocb340vahfpp">
                                                              <span className="font-mono text-[11px] font-bold text-gray-900 group-hover:text-blue-600 transition-colors" data-id="element-5313" data-mp="1gsiamnlecml1">
                                                                {q.questionId}
                                                              </span>
                                                              <Badge variant={q.verdict === 'FAIL' ? 'danger' : 'warning'} size="sm" data-id="element-5314" data-mp="124ngd2rqqa5u">
                                                                {q.verdict}
                                                              </Badge>
                                                              <span className="text-[9px] text-gray-500 font-medium hidden sm:inline" data-id="element-5315" data-mp="2ehlvfpk0bcik">
                                                                {q.blockerCount}{' '}
                                                                blocker
                                                                {q.blockerCount !== 1 ? 's' : ''}
                                                                {q.warningCount > 0 && ` · ${q.warningCount} waarschuwing${q.warningCount !== 1 ? 'en' : ''}`}
                                                              </span>
                                                            </div>
                                                            {firstBlocker && <p className="text-[11px] text-gray-600 truncate" data-id="element-5316" data-mp="3uicattc5jqn">
                                                                <span className="font-mono font-semibold text-[9px] bg-gray-100 px-1 py-0.5 rounded text-gray-500 mr-1.5" data-id="element-5317" data-mp="3v1uunc5yg385">
                                                                  {firstBlocker.code}
                                                                </span>
                                                                {firstBlocker.message}
                                                              </p>}
                                                          </div>
                                                          <ArrowRight className="w-3 h-3 text-gray-300 group-hover:text-red-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" data-id="element-5318" data-mp="27jbjuvebknsy" />
                                                        </div>;
                                      })}
                                                    {qs.length > 5 && <div className="ml-4 sm:ml-20 mr-4 pt-1" data-id="element-5319" data-mp="2qhebzj25z0uz">
                                                        <button onClick={() => onNavigateToQuestion(fam.familyId)} className="text-[10px] font-medium text-gray-500 hover:text-blue-600 flex items-center gap-1" data-id="element-5320" data-mp="2litdpp6h2r96">
                                                          + {qs.length - 5} meer
                                                          vragen — open seed in
                                                          Vragen tab{' '}
                                                          <ArrowRight className="w-3 h-3" data-id="element-5321" data-mp="i2y0mb3cvcg0" />
                                                        </button>
                                                      </div>}
                                                  </>;
                                  })()}
                                            </div>
                                          </motion.div>}
                                      </AnimatePresence>
                                    </div>;
                        })}
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>;
              })}
              </div>
            </Card>
          </div>
        </section>

        {/* ZONE B: Diagnose */}
        <section className="relative" data-id="element-5322" data-mp="3kthuxipaclwh">
          <header className="flex items-start gap-4 mb-6" data-id="element-5323" data-mp="3fvg3oahphg9x">
            <div className="relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 text-sm font-bold border-4 border-white shrink-0 hidden sm:flex shadow-sm" data-id="element-5324" data-mp="3fvzhpg48jkio">
              2
            </div>
            <div className="pt-1 w-full" data-id="element-5325" data-mp="zectnxfzgff">
              <button onClick={() => setIsDiagnoseOpen(!isDiagnoseOpen)} className="flex items-center justify-between w-full text-left group" data-id="element-5326" data-mp="10fn3q6mvyw3">
                <div data-id="element-5327" data-mp="3ob5njfi4zm62">
                  <h2 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors" data-id="element-5328" data-mp="3obyjdlfk4r9f">
                    Diagnose
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5" data-id="element-5329" data-mp="3edrfo4aa45pd">
                    Waar in de pipeline ontstaan de bevindingen?
                  </p>
                </div>
                <div className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-5330" data-mp="35dcjuazov87m">
                  {isDiagnoseOpen ? <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-5331" data-mp="39e67d397ykzz" /> : <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-5332" data-mp="13lg4srebbg83" />}
                </div>
              </button>
            </div>
          </header>

          <AnimatePresence initial={false} data-id="element-5333" data-mp="17l8ghcz062ho">
            {isDiagnoseOpen && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.2
          }} className="sm:ml-12 overflow-hidden" data-id="element-5334" data-mp="iw5i7onuv4s5">
                <div className="pb-2" data-id="element-5335" data-mp="3rbfjwvnu27ze">
                  <div className="space-y-6" data-id="element-5336" data-mp="3mddy42z0vuk8">
                    {/* Pipeline Stages */}
                    <Card className="p-5 shadow-sm border-gray-200/60" data-id="element-5337" data-mp="3hekia3w9jrgw">
                      <div className="mb-5" data-id="element-5338" data-mp="3hcg8547rtw86">
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-5339" data-mp="3cfzmqhryhvys">
                          <Activity className="w-4 h-4 text-blue-500" data-id="element-5340" data-mp="38dme9lltrjyb" />{' '}
                          Pipeline Fasen
                        </h3>
                        <p className="text-[11px] text-gray-500 mt-1" data-id="element-5341" data-mp="1nvk6thkput68">
                          Aantal individuele bevindingen per controle-fase.
                        </p>
                      </div>
                      <div className="space-y-2.5" data-id="element-5342" data-mp="1wuffr1f5ahd8">
                        {PIPELINE_STAGES.map(stage => {
                      const count = pipelineIssueCounts?.[stage.domain] ?? 0;
                      const status = !pipelineIssueCounts ? 'pending' : count === 0 ? 'ok' : count <= 5 ? 'warning' : 'error';
                      const statusConfig = {
                        ok: {
                          dot: 'bg-emerald-500',
                          border: 'border-emerald-100',
                          bg: 'bg-emerald-50/30'
                        },
                        warning: {
                          dot: 'bg-amber-500',
                          border: 'border-amber-100',
                          bg: 'bg-amber-50/30'
                        },
                        error: {
                          dot: 'bg-red-500',
                          border: 'border-red-100',
                          bg: 'bg-red-50/30'
                        },
                        pending: {
                          dot: 'bg-gray-300',
                          border: 'border-gray-100',
                          bg: 'bg-gray-50/50'
                        }
                      }[status];
                      return <div key={stage.name} className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${statusConfig.bg} ${statusConfig.border}`} data-id="element-5343" data-mp="1ktpjhdv79qdg">
                              <div className="flex items-center gap-3" data-id="element-5344" data-mp="2xv3t3t0htyzc">
                                <div className={`w-2 h-2 rounded-full shadow-sm ${statusConfig.dot}`} data-id="element-5345" data-mp="32tf1dzb541fg" />
                                <span className="text-sm font-medium text-gray-700" data-id="element-5346" data-mp="2ts76zsbqvkqn">
                                  {stage.label}
                                </span>
                              </div>
                              {status !== 'pending' && <Badge variant={status === 'ok' ? 'success' : status === 'warning' ? 'warning' : 'danger'} size="sm" data-id="element-5347" data-mp="37qyhl4reeoa1">
                                  {count === 0 ? '0 bevindingen' : `${count} bevindingen`}
                                </Badge>}
                            </div>;
                    })}
                      </div>
                    </Card>

                    {/* P-9: Guard Runtime Rejections */}
                    <Card className="p-5 shadow-sm border-gray-200/60" data-id="element-5348" data-mp="2j3un5lpp0es4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-5349" data-mp="2j4njrtuxtnwj">
                        <Shield className="w-4 h-4 text-orange-500" data-id="element-5350" data-mp="2e6ui9e4kxi2l" /> Runtime
                        Guard
                      </h3>
                      <p className="text-[11px] text-gray-500 mb-5" data-id="element-5351" data-mp="12pco96csvwt9">
                        Vragen afgewezen door de pedagogische guard tijdens live
                        oefensessies.
                      </p>

                      {guardSummary.totalRejections === 0 ? <div className="bg-emerald-50/50 rounded-xl p-5 text-center border border-emerald-100" data-id="element-5352" data-mp="3a9jgi59hdr6k">
                          <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-2" data-id="element-5353" data-mp="3jcauoctqr11w" />
                          <p className="text-xs font-medium text-emerald-800" data-id="element-5354" data-mp="3o2r9o1d7muqd">
                            Geen runtime afwijzingen
                          </p>
                        </div> : <div className="space-y-4" data-id="element-5355" data-mp="1v1dihyed1tt">
                          <div className="grid grid-cols-2 gap-3" data-id="element-5356" data-mp="1tq2bx2mgrso">
                            <div className="bg-red-50/50 rounded-xl p-3 border border-red-100 text-center" data-id="element-5357" data-mp="5y7jdohlkqzr">
                              <p className="text-xl font-bold text-red-700" data-id="element-5358" data-mp="auxzucxbdolp">
                                {guardSummary.totalAffectedQuestions}
                              </p>
                              <p className="text-[10px] font-medium text-red-600/80 uppercase tracking-wider mt-0.5" data-id="element-5359" data-mp="2cmswpmewvojv">
                                Vragen
                              </p>
                            </div>
                            <div className="bg-orange-50/50 rounded-xl p-3 border border-orange-100 text-center" data-id="element-5360" data-mp="2tb04l2ayxfh1">
                              <p className="text-xl font-bold text-orange-700" data-id="element-5361" data-mp="2y9l4f0mb5x4y">
                                {guardSummary.totalRejections}
                              </p>
                              <p className="text-[10px] font-medium text-orange-600/80 uppercase tracking-wider mt-0.5" data-id="element-5362" data-mp="25obkc9yhlax9">
                                Afwijzingen
                              </p>
                            </div>
                          </div>

                          {guardSummary.reasonBreakdown.length > 0 && <div className="space-y-2" data-id="element-5363" data-mp="2kdpuhk9vzwdp">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-5364" data-mp="1mvpqtwb1xwfu">
                                Top redenen
                              </p>
                              {guardSummary.reasonBreakdown.slice(0, 3).map(r => <div key={r.reason} className="flex items-start gap-2 text-[11px]" data-id="element-5365" data-mp="4x1jle87u5v1">
                                    <span className="font-mono text-red-600 font-semibold flex-shrink-0 bg-red-50 px-1 rounded" data-id="element-5366" data-mp="3m6zjxsj9jsi4">
                                      {r.count}×
                                    </span>
                                    <span className="text-gray-600 leading-tight pt-0.5" data-id="element-5367" data-mp="3de5y1c9b9uva">
                                      {r.description}
                                    </span>
                                  </div>)}
                            </div>}

                          {guardSummary.issues.length > 0 && <div className="space-y-2" data-id="element-5368" data-mp="2ou2lzlw9bd1v">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-5369" data-mp="2oshrtxdutw4j">
                                Meest afgewezen
                              </p>
                              {guardSummary.issues.slice(0, 3).map(issue => <div key={issue.questionId} className="flex items-center justify-between text-[10px] bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-100" data-id="element-5370" data-mp="2jvrc5b5yp2jn">
                                  <span className="font-mono font-medium text-gray-700 truncate mr-2" title={issue.questionId} data-id="element-5371" data-mp="2ysiffvirayej">
                                    {issue.questionId}
                                  </span>
                                  <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-5372" data-mp="3vaoflq2w7uaj">
                                    {issue.fatalCount > 0 && <span className="text-red-600 font-bold bg-red-100/50 px-1.5 rounded" data-id="element-5373" data-mp="1ew2v7o0l2uwp">
                                        {issue.fatalCount}F
                                      </span>}
                                    {issue.softCount > 0 && <span className="text-amber-600 font-bold bg-amber-100/50 px-1.5 rounded" data-id="element-5374" data-mp="10v8ln21sunwg">
                                        {issue.softCount}S
                                      </span>}
                                  </div>
                                </div>)}
                            </div>}

                          {guardSummary.dataRange && <p className="text-[10px] text-gray-400 pt-2 border-t border-gray-100 text-center" data-id="element-5375" data-mp="2hajyb2c2ivmy">
                              Data:{' '}
                              {new Date(guardSummary.dataRange.from).toLocaleDateString('nl-NL')}{' '}
                              —{' '}
                              {new Date(guardSummary.dataRange.to).toLocaleDateString('nl-NL')}
                            </p>}
                        </div>}
                    </Card>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </section>

        {/* ZONE C: Systeem-overzicht */}
        <section className="relative" data-id="element-5376" data-mp="1njp3byyshcji">
          <header className="flex items-start gap-4 mb-6" data-id="element-5377" data-mp="1ninrvtdv2o19">
            <div className="relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-600 text-sm font-bold border-4 border-white shrink-0 hidden sm:flex shadow-sm" data-id="element-5378" data-mp="1sjl4zrk0krvw">
              3
            </div>
            <div className="pt-1 w-full" data-id="element-5379" data-mp="27mv0ok096c31">
              <button onClick={() => setIsSysteemOpen(!isSysteemOpen)} className="flex items-center justify-between w-full text-left group" data-id="element-5380" data-mp="22mqj0qnlzbb8">
                <div data-id="element-5381" data-mp="1tlsdg3xiuvqj">
                  <h2 className="text-lg font-bold text-gray-900 group-hover:text-slate-600 transition-colors" data-id="element-5382" data-mp="1tmk7397k6je6">
                    Systeem-overzicht
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5" data-id="element-5383" data-mp="2lcycsi5f4per">
                    Hoe staat het systeem er in zijn totaliteit voor?
                  </p>
                </div>
                <div className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-5384" data-mp="2gk58fvdv3j37">
                  {isSysteemOpen ? <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-5385" data-mp="392alzi0qwxh2" /> : <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-5386" data-mp="34bu8xz0u7dv8" />}
                </div>
              </button>
            </div>
          </header>

          <AnimatePresence initial={false} data-id="element-5387" data-mp="12f23z1hv26ni">
            {isSysteemOpen && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.2
          }} className="sm:ml-12 overflow-hidden" data-id="element-5388" data-mp="17c21up4k433j">
                <div className="space-y-6 pb-2" data-id="element-5389" data-mp="9qwk0gymsloq">
                  {/* Stats — question-level counts */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5390" data-mp="ephcfu7n612m">
                    <StatCard label="Totaal Vragen" value={stats.total.toLocaleString('nl-NL')} icon={<FileText className="w-5 h-5" data-id="element-5392" data-mp="290ct7jszcmgh" />} color="blue" subtitle="Unieke vragen in het systeem" data-id="element-5391" data-mp="244p1oxr0zh7t" />
                    <StatCard label="Geslaagd (PASS)" value={stats.pass.toLocaleString('nl-NL')} icon={<CheckCircle2 className="w-5 h-5" data-id="element-5394" data-mp="2dydwn5uh6cc" />} color="emerald" subtitle={`${Math.round(stats.pass / Math.max(stats.total, 1) * 100)}% van alle vragen`} data-id="element-5393" data-mp="3onhrmgwt73vo" />
                    <StatCard label="Afgekeurd (FAIL)" value={stats.fail.toLocaleString('nl-NL')} icon={<XCircle className="w-5 h-5" data-id="element-5396" data-mp="l4y8c3w3z8d0" />} color={stats.fail === 0 ? 'emerald' : 'red'} subtitle={`${stats.blockerCount} blocker-bevindingen`} data-id="element-5395" data-mp="c3hwrstk5077" />
                    <StatCard label="Review Nodig" value={stats.review.toLocaleString('nl-NL')} icon={<AlertTriangle className="w-5 h-5" data-id="element-5398" data-mp="212vkxfuj0wfn" />} color={stats.review === 0 ? 'emerald' : 'amber'} subtitle={`${stats.warningCount} verbeterpunten`} data-id="element-5397" data-mp="2mwuib92moa5o" />
                  </div>

                  {/* 5-level severity tiles */}
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5" data-id="element-5399" data-mp="2fri823qeashf">
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4" data-id="element-5400" data-mp="1i5ufjtx28ub6">
                      Bevindingen per ernst
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-id="element-5401" data-mp="1s27hff2reeh0">
                      {[{
                    key: 'critical' as const,
                    label: 'Kritiek',
                    Icon: AlertOctagon,
                    bg: 'bg-red-50/50',
                    border: 'border-red-100',
                    text: 'text-red-700',
                    iconClass: 'text-red-600',
                    hint: 'Blocker · ernst ≥90'
                  }, {
                    key: 'blocker' as const,
                    label: 'Blocker',
                    Icon: XCircle,
                    bg: 'bg-orange-50/50',
                    border: 'border-orange-100',
                    text: 'text-orange-700',
                    iconClass: 'text-orange-600',
                    hint: 'Blocker · ernst 70-89'
                  }, {
                    key: 'review' as const,
                    label: 'Review',
                    Icon: Eye,
                    bg: 'bg-yellow-50/50',
                    border: 'border-yellow-100',
                    text: 'text-yellow-700',
                    iconClass: 'text-yellow-600',
                    hint: 'Coach · ernst ≥45'
                  }, {
                    key: 'coach' as const,
                    label: 'Coach',
                    Icon: AlertTriangle,
                    bg: 'bg-blue-50/50',
                    border: 'border-blue-100',
                    text: 'text-blue-700',
                    iconClass: 'text-blue-600',
                    hint: 'Coach · ernst <45'
                  }, {
                    key: 'info' as const,
                    label: 'Info',
                    Icon: Info,
                    bg: 'bg-slate-50/50',
                    border: 'border-slate-100',
                    text: 'text-slate-700',
                    iconClass: 'text-slate-500',
                    hint: 'Observatie'
                  }].map(tier => {
                    const count = stats.severityCounts?.[tier.key] ?? 0;
                    const TierIcon = tier.Icon;
                    return <div key={tier.key} className={`flex flex-col p-3 rounded-xl border ${tier.bg} ${tier.border} transition-transform hover:scale-[1.02]`} title={tier.hint} data-id="element-5402" data-mp="3tsuov6kkxdij">
                            <TierIcon className={`w-5 h-5 mb-2 ${tier.iconClass}`} data-id="element-5403" data-mp="be291zrdhir5" />
                            <p className={`text-2xl font-bold leading-none ${tier.text}`} data-id="element-5404" data-mp="1o0k75r73wb6">
                              {count}
                            </p>
                            <p className={`text-[11px] font-semibold uppercase tracking-wider ${tier.text} mt-1 opacity-80`} data-id="element-5405" data-mp="uc65btm5vnbj">
                              {tier.label}
                            </p>
                          </div>;
                  })}
                    </div>
                  </div>

                  {/* Explainer legend */}
                  <div className="flex items-start gap-3 px-5 py-4 rounded-xl bg-blue-50/50 border border-blue-100" data-id="element-5406" data-mp="zwvn10xkqsdx">
                    <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-5407" data-mp="v1gb4ej4n6k5" />
                    <div className="text-sm text-blue-800/90 leading-relaxed" data-id="element-5408" data-mp="q1tvjyftte86">
                      <span className="font-semibold text-blue-900" data-id="element-5409" data-mp="l8hi8kglktuq">
                        Hoe lees je deze cijfers?
                      </span>{' '}
                      <span className="font-semibold text-red-700" data-id="element-5410" data-mp="c8uj65bcc04u">
                        Afgekeurd
                      </span>{' '}
                      en{' '}
                      <span className="font-semibold text-amber-700" data-id="element-5411" data-mp="7981njohbxq8">
                        Review
                      </span>{' '}
                      tellen het aantal <em data-id="element-5412" data-mp="2dyjk7e65802r">vragen</em> met die status. Elke
                      vraag kan meerdere <em data-id="element-5413" data-mp="2ix4llgx4spss">bevindingen</em> hebben
                      (Kritiek/Blocker/Review/Coach/Info). De Pipeline Fasen
                      hierboven tonen het totaal aantal bevindingen per
                      controle-fase.
                    </div>
                  </div>

                  {/* System Coverage */}
                  <Card className="p-6 shadow-sm border-gray-200/60" data-id="element-5414" data-mp="1v823y92hywya">
                    <div className="mb-6" data-id="element-5415" data-mp="206mvzl7liabn">
                      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-5416" data-mp="207oiclu2aj8o">
                        <Database className="w-5 h-5 text-indigo-500" data-id="element-5417" data-mp="2475ntf9hngdu" />{' '}
                        Systeemdekking
                      </h3>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-5418" data-mp="290ru8pp26o17">
                        Overzicht van beschikbare componenten en hun gebruik in
                        het vragenbestand.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-id="element-5419" data-mp="xd7xzupztr2j">
                      <div className="bg-indigo-50/40 rounded-xl p-4 border border-indigo-100 text-center" data-id="element-5420" data-mp="sf6gvfc4o1tp">
                        <p className="text-3xl font-bold text-indigo-700" data-id="element-5421" data-mp="174cdqwnhtfyf">
                          {systemStats.visualTypeCount}
                        </p>
                        <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600/80 mt-1" data-id="element-5422" data-mp="12b13smndwj2a">
                          Visual Types
                        </p>
                      </div>
                      <div className="bg-purple-50/40 rounded-xl p-4 border border-purple-100 text-center" data-id="element-5423" data-mp="1b7s6vj0csy6d">
                        <p className="text-3xl font-bold text-purple-700" data-id="element-5424" data-mp="1l3lsdrw1w20a">
                          {systemStats.questionTypeCount}
                        </p>
                        <p className="text-xs font-semibold uppercase tracking-wider text-purple-600/80 mt-1" data-id="element-5425" data-mp="3rul86jqdje1z">
                          Vraagtypes
                        </p>
                      </div>
                      <div className="bg-blue-50/40 rounded-xl p-4 border border-blue-100 text-center" data-id="element-5426" data-mp="22dfwuyl0un3i">
                        <p className="text-3xl font-bold text-blue-700" data-id="element-5427" data-mp="27b7nlp9xk364">
                          {systemStats.familyCount}
                        </p>
                        <p className="text-xs font-semibold uppercase tracking-wider text-blue-600/80 mt-1" data-id="element-5428" data-mp="1xlo2ecyqvx83">
                          Families
                        </p>
                      </div>
                      <div className="bg-emerald-50/40 rounded-xl p-4 border border-emerald-100 text-center" data-id="element-5429" data-mp="1noji6iogmbtx">
                        <p className="text-3xl font-bold text-emerald-700" data-id="element-5430" data-mp="19p1vc8uq58n7">
                          {systemStats.moduleCount}
                        </p>
                        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600/80 mt-1" data-id="element-5431" data-mp="1ep6fq9yr6nip">
                          Modules
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8" data-id="element-5432" data-mp="37kqlmn7e2pva">
                      <div data-id="element-5433" data-mp="32l48edrk99mh">
                        <h4 className="text-xs font-bold text-gray-400 mb-4 uppercase tracking-wider" data-id="element-5434" data-mp="32kusjj08ciui">
                          Top 5 Vraagtypes
                        </h4>
                        <div className="space-y-2.5" data-id="element-5435" data-mp="2smya064yqlmd">
                          {Array.from(questionTypeDistribution.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([type, count]) => <div key={type} className="flex items-center justify-between text-sm bg-gray-50/50 px-3 py-2 rounded-lg border border-gray-100" data-id="element-5436" data-mp="3ldydmfzdtndi">
                                <span className="text-gray-700 font-mono text-xs font-medium truncate mr-2" data-id="element-5437" data-mp="cwu1wppo8cgy">
                                  {type}
                                </span>
                                <span className="font-bold text-gray-900 bg-white shadow-sm border border-gray-200 px-2 py-0.5 rounded text-xs" data-id="element-5438" data-mp="gehr0cjbculs">
                                  {count}
                                </span>
                              </div>)}
                        </div>
                      </div>
                      <div data-id="element-5439" data-mp="ubek6kmwmo5u">
                        <h4 className="text-xs font-bold text-gray-400 mb-4 uppercase tracking-wider" data-id="element-5440" data-mp="zcu2w61m0il2">
                          Top 5 Visuals
                        </h4>
                        <div className="space-y-2.5" data-id="element-5441" data-mp="13c30xn3on2mf">
                          {Array.from(visualTypeDistribution.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([type, count]) => <div key={type} className="flex items-center justify-between text-sm bg-gray-50/50 px-3 py-2 rounded-lg border border-gray-100" data-id="element-5442" data-mp="1i4wqk1co4z4t">
                                <span className="text-gray-700 font-mono text-xs font-medium truncate mr-2" data-id="element-5443" data-mp="23ejwn87tp7ha">
                                  {type}
                                </span>
                                <span className="font-bold text-gray-900 bg-white shadow-sm border border-gray-200 px-2 py-0.5 rounded text-xs" data-id="element-5444" data-mp="2ceyjwd4ckwnf">
                                  {count}
                                </span>
                              </div>)}
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </motion.div>}
          </AnimatePresence>
        </section>
      </div>
    </div>;
}